<template>
    <div class="position-relative">
        <p class="mb-1 h5">Try submitting your inventory using <strong style="text-decoration: underline; text-decoration-thickness: 4px; text-decoration-color: var(--secondary);">Wisk Audio Inventory</strong>.</p>
        <div class="mb-2">
          <span class="text-muted small">
            Supported files: .mp3, .ogg, .wav, .webm, .m4a
          </span>
          <br/>
          <span class="text-muted small">
            Max file size: 25mb
          </span>
          <br/>
          <span class="text-muted small">
            Instructions:
            * Using headphones / handsfree is recommended.
            * Make sure to speak clearly.
            * Make sure your microphone picks up your voice properly and with good quality.
            * Try to mention full inventory item name while counting.
            * Supported countings: unit (2 units of Jack Daniel's Honey), case (1 case of Jack Daniel's Honey), manual (200 ml of Jack Daniel's Honey).
            * Supported manual measurement units based on item type (volume, weight or unit): Milliliter, Liter, Fluid Ounce, Gallon, Quart, Pint, Cup, Tablespoon, Teaspoon, Gram, Kilogram, Pound (Lb), Dry Ounce, Unit, Each.
          </span>
        </div>
        <div v-if="!error">
          <b-button variant="primary" @click="isRecording ? stopRecording() : startRecording()" class="" :title="isRecording ? 'Stop recording' : 'Start recording'">
            {{ isRecording ? 'Stop recording' : 'Start recording' }}
          </b-button>
          <b-button variant="outline-primary" v-if="isRecording" @click="cancelRecording" class="ms-2" title="Cancel recording">
              Cancel Recording
          </b-button>
          <div v-if="!isRecording" class="d-inline-block ms-2">
            <input
              type="file"
              ref="fileInput"
              @input="handleFileUpload"
              accept=".mp3,.ogg,.wav,.webm,.m4a"
              class="d-none"
            >
            <b-button variant="outline-primary" @click="$refs.fileInput.click()" title="Upload audio file">
              Upload Audio
            </b-button>
          </div>
        </div>
        <div v-else>
          <p>Error while sending audio. Please try again.</p>
          <b-button @click="() => { error = false; onSubmit(currentFile) }">Try again</b-button>
          <b-button variant="outline-primary" @click="cancelRecording" class="ms-2" title="Cancel">
              Cancel
          </b-button>
        </div>

        <div class="mt-2" v-if="savedAudioFileAddress">
          <p class="mb-1 h6">Current Audio:</p>
          <audio ref="audioPlayer" controls class="w-100" :key="savedAudioFileAddress">
            <source :src="savedAudioFileAddress">
            Your browser does not support the audio tag.
          </audio>

          <!-- Add transcription display -->
          <div class="mt-3 transcription-wrapper">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <p class="mb-0 h6">Transcription:</p>
              <div class="view-toggle">
                <b-form-radio-group size="sm" buttons v-model="viewMode" button-variant="outline-primary">
                  <b-form-radio value="segments"> Segments </b-form-radio>
                  <b-form-radio value="paragraph"> Paragraph </b-form-radio>
                  <b-form-radio value="items"> Items </b-form-radio>
                </b-form-radio-group>
              </div>
            </div>

            <!-- Segments View -->
            <div v-if="viewMode === 'segments'" class="transcription-container">
              <div
                v-for="(segment, index) in transcription.segments"
                :key="index"
                class="transcription-segment mb-2"
                @click="seekToTimestamp(segment.start)"
              >
                <span class="timestamp text-muted me-2">{{ formatTimestamp(segment.start) }}</span>
                <span class="transcription-text">{{ segment.text }}</span>
              </div>
            </div>

            <!-- Paragraph View -->
            <div v-else-if="viewMode === 'paragraph'" class="transcription-container paragraph-view">
              <p class="mb-0">
                <span
                  v-for="(segment, index) in transcription.segments"
                  :key="index"
                  class="transcription-text"
                  @click="seekToTimestamp(segment.start)"
                  :title="formatTimestamp(segment.start)"
                >
                  {{ segment.text }}{{ index < transcription.length - 1 ? ' ' : '' }}
                </span>
              </p>
            </div>

            <!-- Items View -->
            <div v-else-if="viewMode === 'items'" class="transcription-container">
              <div v-for="item in transcription.items" :key="item.variation_id"
                @click="seekToTimestamp(item.timestamp)" class="transcription-segment">
                <span class="timestamp text-muted me-2">{{ formatTimestamp(item.timestamp) }}</span>
                <span class="transcription-text">
                  {{ item.item ? item.item.title : 'Variation ID: ' + item.variation_id + ' (unknown)' }}
                </span>
                <span class="text-muted small ms-1">
                  {{ item.measurement }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <wiskLoading :loading="loading" />
    </div>
    <confirm ref="confirm" />
</template>

<script>
import { mapState } from 'vuex'
import merge from 'lodash.merge'
import api from '@/api'
import { prepareVariantAsItem } from '@/modules/utils'

export default {
  name: 'InventoryAudio',
  emits: ['save', 'refreshEntry'],
  props: { data: Object },
  data() {
    return {
      isRecording: false,
      recorder: null,
      shouldSave: true,
      audioChunks: [],
      currentFile: null,
      error: false,
      loading: false,
      viewMode: 'segments',
    }
  },
  computed: {
    ...mapState(['itemVariants', 'itemVariantsById', 'bottlesById', 'venue', 'user']),
    transcription() {
      if (this.data.audio_transcript) {
        const data = JSON.parse(this.data.audio_transcript)
        return {
          ...data,
          items: data.items.sort((a, b) => a.timestamp - b.timestamp).map(i => ({
            ...i,
            item: prepareVariantAsItem(this.itemVariantsById[i.variation_id], this.$store.state)
          }))
        }
      }
      return { segments: [], items: [] }
    },
    items() {
      const variations = this.itemVariants
        .filter(i => !this.bottlesById[i.item_id]?.archived && !i.archived && (i.inventoriable || i.inventoriable_as_cases))
        .map(v => {
          const variation = prepareVariantAsItem(v, this.$store.state)
          return {
            item_title: variation.title,
            item_id: variation.item_id,
            variation_id: variation.item_distributor_id,
            distributor_id: variation.distributor.id,
            distributor_title: variation.distributor.title,
            measurement: variation.measurement,
          }
        })
      return variations
    },
    savedAudioFileAddress() {
      if (this.data.audio_file) {
        return `https://wisk.s3.amazonaws.com/${this.data.audio_file}`
      }
      return null
    }
  },
  methods: {
    onSubmit(file) {
      if (file.size > 25 * 1024 * 1024) {
        alert('File size is too large. Max supported file size is 25MB. Please try again with a smaller file.')
        return
      }
      // supported file formats by model
      // .flac,.mp3,.mp4,.mpeg,.mpga,.m4a,.ogg,.wav,.webm
      this.loading = true
      this.currentFile = file
      const formData = new FormData()
      formData.append('file', file)
      formData.append('items', JSON.stringify(this.items))
      formData.append('inventory_location', JSON.stringify(this.data))
      api.inventoryAudio(formData).then(response => {
        this.loading = false
        this.currentFile = null
        this.$refs.fileInput.value = ''
        let operations = [
          { type: 'audio_file', value: response.data.audio_file_s3_key },
          {
            type: 'audio_transcript',
            value: JSON.stringify({
              segments: response.data.transcription,
              items: response.data.items.map(i => ({
                variation_id: i.item.variation_id,
                measurement: `${i.inventory.quantity} ` + (i.inventory.type === 'unit' ? `unit${i.inventory.quantity > 1 ? 's' : ''}` : i.inventory.type === 'case' ? `case${i.inventory.quantity > 1 ? 's' : ''}` : i.inventory.unit_of_measurement),
                timestamp: i.timestamp,
              }))
            })
          }
        ]
        let newEntries = []
        response.data.items.forEach((item, index) => {
          const itemVariation = prepareVariantAsItem(this.itemVariantsById[item.item.variation_id], this.$store.state)
          if (itemVariation) {
            let measurement = {}
            if (item.inventory.type === 'unit') {
              measurement = { type: 'unit', quantity: item.inventory.quantity }
            } else if (item.inventory.type === 'case' && itemVariation.inventoriable_as_cases) {
              measurement = { type: 'case', quantity: item.inventory.quantity, case_size: itemVariation.case_size }
            } else {
              measurement = { type: 'manual', quantity: item.inventory.quantity, unit_of_measurement: item.inventory.unit_of_measurement }
            }
            const id = new Date().getTime() * 1000 + index

            let entry = {
              location_id: this.data.id,
              inventory_order: id,
              measurement: {
                measurement,
                timestamp: new Date().toISOString(),
                item_distributor_id: item.item.variation_id,
                item_id: item.item.item_id,
                order: id,
                item_distributor_measurement: itemVariation.measurement,
                user_id: this.user.id
              },
              venue_id: this.venue.id,
              inventory_id: this.data.inventory_id,
              user_id: this.user.id
            },
            operation = {
              type: 'item_add',
              value: {
                item_id: entry.measurement.item_id,
                item_distributor_id: entry.measurement.item_distributor_id,
                measurement_id: id,
                order: id,
                id,
                item_distributor_measurement: itemVariation.measurement,
                user_id: this.user.id,
                input_source: {
                  type: 'audio'
                },
                previous_inventory_order: entry.measurement.order,
                timestamp: new Date().toISOString(),
                hidden: false,
                ...entry.measurement,
                inventory_id: this.data.inventoryId,
                inventory_order: id,
                case_size: itemVariation.case_size
              }
            }
            operations.push(operation)

            newEntries.push(merge({}, entry, {
              inventory_order: id,
              inventory_id: this.data.inventoryId,
              id,
              measurement: {
                ...entry.measurement,
                inventory_id: this.data.inventoryId,
                inventory_order: id,
                id,
                case_size: itemVariation.case_size,
                order: id,
                measurement_id: id,
                previous_inventory_order: entry.measurement.order
              }
            }))
          }
        })
        this.$emit('save', operations)
        newEntries.forEach((entry) => {
          this.$emit('refreshEntry', entry)
        })
      }).catch((e) => {
        console.log(e)
        this.loading = false
        this.error = true
      })
    },
    async startRecording() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.error('Media devices not supported')
          return
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Try different MIME types in order of preference
      const mimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/mp3',
        'audio/mpeg'
      ]

      let mimeType = mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || ''

      if (!mimeType) {
        console.error('No supported audio MIME types found')
        return
      }


      const mediaRecorder = new MediaRecorder(stream, {
        mimeType
      })
      this.recorder = mediaRecorder

      mediaRecorder.ondataavailable = (event) => {
          this.audioChunks.push(event.data)
      }

      mediaRecorder.onstop = async () => {
          if (!this.shouldSave) {
            this.audioChunks = []
              this.shouldSave = true
              return
          }

          const audioBlob = new Blob(this.audioChunks, { type: mimeType })
          this.audioChunks = []

          // Generate a timestamp for the filename
          const timestamp = new Date().toISOString().replace(/:/g, '-').replace(/\..+/, '')
          const extension = mimeType.includes('webm') ? 'webm' : 'mp3'
          const filename = `recording_${timestamp}.${extension}`

          // Convert the audioBlob to a File with a name
          const audioFile = new File([audioBlob], filename, { type: mimeType })

          this.onSubmit(audioFile)
      }

      mediaRecorder.start()
      this.isRecording = true
    },
    stopRecording() {
      if (this.recorder) {
        this.recorder.stop()
          this.isRecording = false

          // Stop all tracks of the media stream
          const stream = this.recorder.stream
          stream.getTracks().forEach(track => track.stop())
      }
    },
    cancelRecording() {
      this.shouldSave = false
      if (this.recorder) {
        this.recorder.stop()

        // Stop all tracks of the media stream
        const stream = this.recorder.stream
        stream.getTracks().forEach(track => track.stop())
      }
      this.isRecording = false
      this.error = false
    },
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.$refs.confirm.confirm({
          callback: () => {
            this.onSubmit(file)
          },
          cancelCallback: () => {
            event.target.value = ''
          },
          message: 'Are you sure you want to submit this audio file? ' + file.name,
          title: 'Submit audio file'
        })
      }
    },
    seekToTimestamp(timestamp) {
      const audioPlayer = this.$refs.audioPlayer
      if (audioPlayer) {
        audioPlayer.currentTime = timestamp - 1 > 0 ? timestamp - 1 : 0
        audioPlayer.play()
      }
    },
    formatTimestamp(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.floor(seconds % 60)
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    }
  },
}
</script>

<style>
.transcription-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 200px;
  max-height: 600px;
}

.transcription-container {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
  background-color: #fff;
}

.transcription-segment {
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.transcription-segment:hover, .items-view div:hover {
  background-color: #f8f9fa;
  cursor: pointer;
}

.timestamp {
  font-size: 0.875rem;
}

.paragraph-view .transcription-text {
  cursor: pointer;
  transition: background-color 0.2s;
  padding: 2px 4px;
  border-radius: 2px;
}

.paragraph-view .transcription-text:hover {
  background-color: #00cdf17e;
}

.view-toggle {
  margin-bottom: 0.5rem;
}
</style>